import { useNavigation } from "@react-navigation/native";
import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { ONBOARDING_NAVIGATION_ERROR } from "../../constants/onboardingMessages";
import ScreenNames from "../../constants/routeNames";
import { getNextScreen } from "../../navigation/simpleOnboardingFlow";
import type { SimpleAllergensEnum } from "../../services/backendTypes";
import { selectAllergens, setAllergens } from "../../slices/simpleOnboardingSlice";
import SelectionScreen, { type Option } from "../New_DIY_form/SelectionScreen";

const validationSchema = Yup.object().shape({
  allergens: Yup.array().of(Yup.string()),
});

const SimpleOnboardingAllergensScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const selectedAllergens = useSelector(selectAllergens);

  const options: Option[] = [
    {
      value: "none",
      label: t("simple_onboarding.allergens.none"),
      description: "",
      icon: "CircleCheck",
      iconType: "lucide",
    },
    {
      value: "PEANUTS" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.PEANUTS"),
      description: "",
      icon: "CircleSlash",
      iconType: "lucide",
    },
    {
      value: "TREE_NUTS" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.TREE_NUTS"),
      description: "",
      icon: "TreePine",
      iconType: "lucide",
    },
    {
      value: "SHELLFISH" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.SHELLFISH"),
      description: "",
      icon: "Shell",
      iconType: "lucide",
    },
    {
      value: "FISH" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.FISH"),
      description: "",
      icon: "Fish",
      iconType: "lucide",
    },
    {
      value: "EGG" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.EGG"),
      description: "",
      icon: "Egg",
      iconType: "lucide",
    },
    {
      value: "SOY" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.SOY"),
      description: "",
      icon: "Bean",
      iconType: "lucide",
    },
    {
      value: "SESAME" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.SESAME"),
      description: "",
      icon: "CircleSlash",
      iconType: "lucide",
    },
    {
      value: "WHEAT" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.WHEAT"),
      description: "",
      icon: "Wheat",
      iconType: "lucide",
    },
    {
      value: "MILK" as SimpleAllergensEnum,
      label: t("simple_onboarding.allergens.MILK"),
      description: "",
      icon: "Milk",
      iconType: "lucide",
    },
  ];

  return (
    <Formik
      initialValues={{ allergens: selectedAllergens.length > 0 ? selectedAllergens : ["none"] }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const allergensToSave = values.allergens.filter((a: string) => a !== "none") as SimpleAllergensEnum[];
        dispatch(setAllergens(allergensToSave));
        const nextScreen = getNextScreen(ScreenNames.SimpleOnboarding_4_Allergens);
        if (nextScreen) {
          navigation.navigate(nextScreen as never);
        } else {
          alert(ONBOARDING_NAVIGATION_ERROR);
        }
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => {
        // Special logic for handling "none" selection
        const handleAllergenSelection = (allergenOptions: string[]): void => {
          // Check if "none" was just clicked
          const currentHasNone = values.allergens.includes("none");
          const newHasNone = allergenOptions.includes("none");
          const noneWasJustClicked = !currentHasNone && newHasNone;

          if (noneWasJustClicked && allergenOptions.length > 1) {
            // If "none" was just clicked and there are other selections, clear all and select only "none"
            setFieldValue("allergens", ["none"]);
          } else if (allergenOptions.includes("none") && allergenOptions.length > 1) {
            // If "none" is selected with other options, deselect "none"
            setFieldValue(
              "allergens",
              allergenOptions.filter((o) => o !== "none")
            );
          } else if (allergenOptions.length === 0) {
            // If nothing is selected, default to "none"
            setFieldValue("allergens", ["none"]);
          } else {
            // Normal selection
            setFieldValue("allergens", allergenOptions);
          }
        };

        return (
          <SelectionScreen
            title={t("simple_onboarding.allergens.title")}
            subtitle={t("simple_onboarding.allergens.subtitle")}
            options={options}
            multipleSelection={true}
            selectedOptions={values.allergens}
            setSelectedOptions={handleAllergenSelection}
            onPressNext={handleSubmit}
            onPressBack={() => navigation.goBack()}
            nextButtonDisabled={false} // Always enabled since "none" is pre-selected
            nextButtonTestIdPostfix="allergens"
          />
        );
      }}
    </Formik>
  );
};

export default SimpleOnboardingAllergensScreen;
